import React, {useMemo, useState} from 'react'
import {useForm} from '../../common/hooks/useForm'
import {useNotification} from '../../common/notifications/useNotification'
import {Organisation as OrganisationType} from '../../common/types/organisation'
import {AllOrganisationsDocument, GQL_SearchType, OrganisationByIdDocument, useEditOrganisationMutation} from '../../generated/graphql'
import {Box} from '../../webui/Box'
import {Button} from '../../webui/Button'
import {SaveButton} from '../../webui/buttons/SaveButton'
import {Drawer} from '../../webui/Drawer'
import {MultiSearchSelect, MultiSearchSelectProps, Option} from '../../webui/inputs/SearchSelect'
import {Typography} from '../../webui/Typography'
import {useAllBoxesForSelect} from '../box/gql/useAllBoxesForSelect'
import {useAllUsersForSelect} from '../user/gql/useAllUsersForSelect'
import {EditOrganisationContact} from './EditOrganisationContact'
import {EditOrganisationDetails} from './EditOrganisationDetails'

interface EditOrganisationDrawerProps {
  organisation: OrganisationType
  open: boolean
  onClose: () => void
}

interface EditOrganisationForm {
  boxes: Option[]
  city?: string
  contactEmail: string
  contactName: string
  country?: string
  name: string
  phoneNumber?: string
  users: Option[]
  zipCode?: string
  archived: boolean
}

const usersPageSize = 100

const createInitialEditOrganisationForm = (organisation: OrganisationType): EditOrganisationForm => ({
  boxes: organisation.boxes.map((box) => ({label: box.name, value: box.id})),
  city: organisation.city ?? '',
  contactEmail: organisation.contactEmail ?? '',
  contactName: organisation.contactName ?? '',
  country: organisation.country ?? '',
  name: organisation.name ?? '',
  phoneNumber: organisation.phoneNumber ?? '',
  zipCode: organisation.zipCode ?? '',
  users: organisation.users.map((user) => ({
    label: user.email ?? '',
    value: user.id,
  })),
  archived: (organisation.archivedAt ?? null) !== null,
})

// TODO: Form handling, validation, disable save etc
export const EditOrganisationDrawer: React.FC<EditOrganisationDrawerProps> = ({organisation}) => {
  const [open, setOpen] = useState(false)
  const {form, updateField, reset} = useForm(useMemo(() => createInitialEditOrganisationForm(organisation), [organisation]))

  const [editOrganisation, {loading}] = useEditOrganisationMutation()
  const {data: allBoxes, loading: allBoxesLoading} = useAllBoxesForSelect()

  const [searchUsers, setSearchUsers] = useState('')
  const [loadingMoreUsers, setLoadingMoreUsers] = useState(false)
  const usersSearch = searchUsers
    ? {
        email: {
          query: searchUsers,
          type: GQL_SearchType.Contains,
        },
      }
    : undefined
  const {
    data: allUsers,
    loading: allUsersLoading,
    totalCount: allUsersTotalCount,
    fetchMore: fetchMoreUsers,
  } = useAllUsersForSelect({
    variables: {
      pagination: {
        limit: usersPageSize,
        offset: 0,
      },
      search: usersSearch,
    },
  })

  const {showNotification} = useNotification()

  const onBoxesChange: MultiSearchSelectProps['onChange'] = (value) => {
    updateField('boxes', value.slice())
  }
  const onUsersChange: MultiSearchSelectProps['onChange'] = (value) => {
    updateField('users', value.slice())
  }

  const onUsersMenuScrollToBottom = async () => {
    if (allUsersLoading || loadingMoreUsers || allUsers.length >= allUsersTotalCount) return

    setLoadingMoreUsers(true)
    try {
      await fetchMoreUsers({
        variables: {
          pagination: {
            limit: usersPageSize,
            offset: allUsers.length,
          },
          search: usersSearch,
        },
      })
    } finally {
      setLoadingMoreUsers(false)
    }
  }

  const onOpen = () => {
    setOpen(true)
  }

  const onClose = () => {
    setOpen(false)
  }

  const onCancel = () => {
    reset()
    onClose()
  }

  const onSave = async () => {
    const currentArchived = (organisation.archivedAt ?? null) !== null
    const nextArchived = form.archived

    if (currentArchived !== nextArchived) {
      const ok = window.confirm(nextArchived ? 'Really archive this organisation?' : 'Really un-archive this organisation?')
      if (!ok) return
    }

    const archivedAtPatch = currentArchived === nextArchived ? undefined : nextArchived ? new Date().toISOString() : null

    try {
      const result = await editOrganisation({
        variables: {
          organisationId: organisation.id,
          editOrganisation: {
            name: form.name,
            contactName: form.contactName,
            contactEmail: form.contactEmail,
            city: form.city,
            country: form.country,
            phoneNumber: form.phoneNumber,
            zipCode: form.zipCode,
            boxIds: form.boxes.map((box) => box.value),
            userIds: form.users.map((user) => user.value),
            ...(archivedAtPatch !== undefined ? {archivedAt: archivedAtPatch} : {}),
          },
        },
        refetchQueries: [
          {query: AllOrganisationsDocument},
          {
            query: OrganisationByIdDocument,
            variables: {id: organisation.id},
          },
        ],
      })

      const responseType = result?.data?.editOrganisation?.type

      if (responseType !== 'Success') {
        showNotification({
          message: 'Could not edit organisation.',
          severity: 'error',
        })
        return
      }

      showNotification({
        message: 'Organisation edited.',
        severity: 'success',
      })

      onClose()
    } catch (e) {
      showNotification({
        message: 'Could not edit organisation.',
        severity: 'error',
      })
      console.log('Organisation edit error', e, '--- e')
    }
  }

  return (
    <>
      <Box>
        <Button onClick={onOpen}>Edit</Button>
      </Box>
      <Drawer anchor={'right'} onClose={onClose} open={open}>
        <Box m={2}>
          <Typography variant="h4">Edit organisation "{organisation.name}"</Typography>
          <form noValidate autoComplete="off">
            <Box mt={2} display={'flex'} flexDirection={'column'}>
              <Box>
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    cursor: 'pointer',
                  }}
                >
                  <input type="checkbox" checked={form.archived} onChange={(e) => updateField('archived', e.target.checked)} />
                  <Typography>Archived</Typography>
                </label>
              </Box>

              <Box mt={2} />

              <Box>
                <EditOrganisationDetails form={form} updateField={updateField} />
              </Box>
              <Box mt={2} />
              <Box>
                <EditOrganisationContact form={form} updateField={updateField} />
              </Box>
              <Box mt={2}>
                <MultiSearchSelect
                  closeMenuOnSelect={false}
                  label={'Boxes'}
                  loading={allBoxesLoading}
                  onChange={onBoxesChange}
                  options={allBoxes.map((box) => ({
                    label: box.name,
                    value: box.id,
                  }))}
                  value={form.boxes}
                />
              </Box>
              <Box mt={2}>
                <MultiSearchSelect
                  closeMenuOnSelect={false}
                  label={'Users'}
                  loading={allUsersLoading || loadingMoreUsers}
                  onInputChange={(val) => {
                    setSearchUsers(val)
                  }}
                  onMenuScrollToBottom={onUsersMenuScrollToBottom}
                  onChange={onUsersChange}
                  options={allUsers.map((user) => ({
                    label: user.email ?? '',
                    value: user.id,
                  }))}
                  value={form.users}
                />
              </Box>
            </Box>
          </form>
          <Box display={'flex'} justifyContent={'flex-end'} mt={4}>
            <Button onClick={onCancel}>Cancel</Button>
            <Box ml={2} />
            <SaveButton loading={loading} onClick={onSave} />
          </Box>
        </Box>
      </Drawer>
    </>
  )
}
